import { useState } from 'react';
import { useAuthStore } from '../../stores/authStore';
import { api } from '../../lib/api';
import PasswordStrengthMeter from '../../components/auth/PasswordStrengthMeter';

export default function SecurityPage() {
  const { user, checkAuth } = useAuthStore();

  // Password change state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordSuccess, setPasswordSuccess] = useState<string | null>(null);

  // MFA state
  const [mfaLoading, setMfaLoading] = useState(false);
  const [mfaError, setMfaError] = useState<string | null>(null);
  const [mfaSetupData, setMfaSetupData] = useState<{
    secret: string;
    qrCode: string;
  } | null>(null);
  const [mfaVerifyCode, setMfaVerifyCode] = useState('');

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError(null);
    setPasswordSuccess(null);

    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }

    if (newPassword.length < 12) {
      setPasswordError('Password must be at least 12 characters');
      return;
    }

    setPasswordLoading(true);

    try {
      const response = await api.patch('/api/auth/me', {
        currentPassword,
        newPassword,
      });

      if (response.ok) {
        setPasswordSuccess('Password changed successfully');
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        setTimeout(() => setPasswordSuccess(null), 3000);
      } else {
        const data = await response.json() as { error?: string };
        setPasswordError(data.error || 'Failed to change password');
      }
    } catch (err) {
      setPasswordError('Failed to connect to server');
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleSetupMfa = async () => {
    setMfaLoading(true);
    setMfaError(null);

    try {
      const response = await api.post('/api/auth/mfa/setup', {
        method: 'totp',
      });

      if (response.ok) {
        const data = await response.json() as { secret: string; qrCode: string };
        setMfaSetupData({
          secret: data.secret,
          qrCode: data.qrCode,
        });
      } else {
        const data = await response.json() as { error?: string };
        setMfaError(data.error || 'Failed to setup MFA');
      }
    } catch (err) {
      setMfaError('Failed to connect to server');
    } finally {
      setMfaLoading(false);
    }
  };

  const handleVerifyMfa = async (e: React.FormEvent) => {
    e.preventDefault();
    setMfaLoading(true);
    setMfaError(null);

    try {
      const response = await api.post('/api/auth/mfa/verify', {
        code: mfaVerifyCode,
        setup: true,
      });

      if (response.ok) {
        setMfaSetupData(null);
        setMfaVerifyCode('');
        await checkAuth(); // Refresh user data
      } else {
        const data = await response.json() as { error?: string };
        setMfaError(data.error || 'Invalid code');
      }
    } catch (err) {
      setMfaError('Failed to connect to server');
    } finally {
      setMfaLoading(false);
    }
  };

  const handleDisableMfa = async () => {
    if (!confirm('Are you sure you want to disable MFA? This will reduce your account security.')) {
      return;
    }

    setMfaLoading(true);
    setMfaError(null);

    try {
      const response = await api.post('/api/auth/mfa/disable');

      if (response.ok) {
        await checkAuth();
      } else {
        const data = await response.json() as { error?: string };
        setMfaError(data.error || 'Failed to disable MFA');
      }
    } catch (err) {
      setMfaError('Failed to connect to server');
    } finally {
      setMfaLoading(false);
    }
  };

  return (
    <div className="max-w-2xl space-y-6">
      <h1 className="text-2xl font-bold text-gray-900">Security Settings</h1>

      {/* Password Change */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Change Password</h2>

        {passwordError && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">
            {passwordError}
          </div>
        )}

        {passwordSuccess && (
          <div className="mb-4 bg-green-50 border border-green-200 rounded-lg p-3 text-green-700 text-sm">
            {passwordSuccess}
          </div>
        )}

        <form onSubmit={handlePasswordChange} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Current Password
            </label>
            <input
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              New Password
            </label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              minLength={12}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <PasswordStrengthMeter password={newPassword} />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Confirm New Password
            </label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={passwordLoading}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              {passwordLoading ? 'Changing...' : 'Change Password'}
            </button>
          </div>
        </form>
      </div>

      {/* Two-Factor Authentication */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Two-Factor Authentication</h2>

        {mfaError && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">
            {mfaError}
          </div>
        )}

        {user?.mfaEnabled ? (
          <div>
            <div className="flex items-center space-x-2 text-green-600 mb-4">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span className="font-medium">MFA is enabled</span>
            </div>
            <p className="text-sm text-gray-500 mb-4">
              Your account is protected with two-factor authentication.
            </p>
            <button
              onClick={handleDisableMfa}
              disabled={mfaLoading}
              className="px-4 py-2 text-red-600 border border-red-300 rounded-lg hover:bg-red-50 disabled:opacity-50"
            >
              {mfaLoading ? 'Disabling...' : 'Disable MFA'}
            </button>
          </div>
        ) : mfaSetupData ? (
          <div>
            <p className="text-sm text-gray-600 mb-4">
              Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.)
            </p>

            <div className="flex justify-center mb-4">
              <img
                src={mfaSetupData.qrCode}
                alt="MFA QR Code"
                className="w-48 h-48 border rounded-lg"
              />
            </div>

            <div className="mb-4 p-3 bg-gray-100 rounded-lg">
              <p className="text-xs text-gray-500 mb-1">Manual entry code:</p>
              <code className="text-sm font-mono">{mfaSetupData.secret}</code>
            </div>

            <form onSubmit={handleVerifyMfa} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Enter verification code
                </label>
                <input
                  type="text"
                  value={mfaVerifyCode}
                  onChange={(e) => setMfaVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  maxLength={6}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-center text-2xl tracking-widest font-mono"
                />
              </div>

              <div className="flex justify-between">
                <button
                  type="button"
                  onClick={() => setMfaSetupData(null)}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={mfaLoading || mfaVerifyCode.length !== 6}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {mfaLoading ? 'Verifying...' : 'Verify & Enable'}
                </button>
              </div>
            </form>
          </div>
        ) : (
          <div>
            <p className="text-sm text-gray-600 mb-4">
              Add an extra layer of security to your account by enabling two-factor authentication.
            </p>
            <button
              onClick={handleSetupMfa}
              disabled={mfaLoading}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {mfaLoading ? 'Setting up...' : 'Enable MFA'}
            </button>
          </div>
        )}
      </div>

      {/* Sessions */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Active Sessions</h2>
        <p className="text-sm text-gray-500 mb-4">
          View and manage your active login sessions.
        </p>
        <button className="px-4 py-2 text-red-600 border border-red-300 rounded-lg hover:bg-red-50">
          Sign Out All Other Sessions
        </button>
      </div>
    </div>
  );
}
